/**
 * React Query-based initialization service.
 *
 * This module provides initialization functions that use React Query for data
 * fetching and caching for caching. This is the target state for
 * the app initialization for the React Query migration.
 */

import { analytics } from "common/analytics";
import { awaitOnlineStatus } from "common/onlineManagerBridge";
import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import {
  markDeviceAsRegistered,
  setupPushNotificationsAndRegisterDevice,
  shouldRegisterDevice,
} from "common/utils/deviceRegistration";
import { getRedirectUrlWithToken, redirect } from "common/utils/Hooks";
import { default as decode } from "jwt-decode";
import { Farm } from "model/farmTypes";
import {
  getAllArableFieldsFromCache,
  getAllFieldsFromCache,
  getArableFieldsWithNoTilesFromArray,
  getFieldsWithNoTilesFromArray,
  getLatestArableFieldTilesFromArray,
  getLatestFieldTilesFromArray,
} from "model/fieldHelpers";
import { fetchAllAssociatedUsersQuery } from "model/useAccessibleUsersQueries";
import {
  fetchAnimalGroupsByIdQuery,
  prefetchAnimalGroupMoveEventsQuery,
} from "model/useAnimalGroupQueries";
import { fetchArableFieldsByFarmIdQuery } from "model/useArableFieldQueries";
import { fetchAccessibleFarmerProgrammesQuery } from "model/useDashboardQueries";
import { fetchDefaultStockClassesQuery } from "model/useDefaultStockClassesQueries";
import { fetchFarmByIdQuery } from "model/useFarmQueries";
import { fetchFieldsByIdQuery } from "model/useFieldQueries";
import { fetchFileEntriesForUserQuery } from "model/useFileEntryQueries";
import { fetchProgrammeActionsQuery } from "model/useProgrammeActionsQueries";
import { fetchQuickAssessmentResultsQuery } from "model/useQuickAssessmentQueries";
import { User } from "model/userTypes";
import { fetchStockClassesByIdQuery } from "model/useStockClassQueries";
import { fetchSubmittedActionsQuery } from "model/useSubmittedActionsQueries";
import { fetchTilesByIdsQuery } from "model/useTileQueries";
import { fetchUserByIdQuery } from "model/useUserQueries";
import { onlineManager } from "@tanstack/react-query";
import { useAppStore } from "stores/useAppStore";
import { useUIStore } from "stores/useUIStore";
import { JWT } from "./auth";
import {
  invalidateArableFieldData,
  invalidateFieldData,
} from "./fieldOperations";

// Progress steps: Sign in (1) -> Load data (2) -> Load farm fields (3) -> Load farm animals (4) -> Done (5)
const APP_LOADING_STEPS = 5;

/**
 * Initialize the application after login using React Query.
 * Validates the JWT token, fetches user data, and sets up the app state.
 *
 * Initializes the app after login.
 *
 * @param options - Optional configuration
 * @param options.skipLoadingStateReset - If true, don't set loading to idle at the end
 *   (useful when farm initialization will follow immediately)
 * @returns The result containing farms, user data, and whether farm init is needed
 */
export async function initializeAppRQ(options?: {
  skipLoadingStateReset?: boolean;
}): Promise<{
  farms?: Farm[];
  user?: User;
  needsFarmInit?: boolean;
}> {
  const { skipLoadingStateReset = false } = options ?? {};
  const {
    setLoadingStatus,
    setLoadingProgress,
    setLoading,
    setLocale,
    setUser,
    setAuthenticated,
  } = useAppStore.getState();

  setLoading("pending");
  setLoadingProgress(0, APP_LOADING_STEPS);

  setLoadingStatus("Signing in...");
  const token = window.localStorage.getItem("jwt");
  if (!token) {
    setLoading("idle");
    return {};
  }

  const jwtDecoded = decode<JWT>(token);

  // The connectivity bridge resolves the device's real status asynchronously
  // (Network.getStatus). Wait for it before reading onlineManager below, so an
  // offline device takes the offline branch instead of a stale-default online
  // read that fetches over a dead network and hangs the loading screen.
  await awaitOnlineStatus();

  // Offline: hydrate the app from the persisted cache instead of awaiting
  // network fetches that pause (and hang the loading screen) while offline.
  if (!onlineManager.isOnline()) {
    const cachedUser = queryClient.getQueryData<User>(
      queryKeys.user.detail(jwtDecoded.user)
    );
    if (!cachedUser) {
      setLoading("idle");
      return {};
    }
    setUser(cachedUser);
    if (cachedUser.lang) {
      setLocale(cachedUser.lang);
    }
    setAuthenticated(true);

    const offlineFarmIds = cachedUser.farms;
    const offlineFarms = (offlineFarmIds ?? [])
      .map((id) => queryClient.getQueryData<Farm>(queryKeys.farms.detail(id)))
      .filter((f): f is Farm => !!f);
    const isCorporate = !!(
      cachedUser.corporations && cachedUser.corporations.length > 0
    );
    const hasFarmsOffline = !!(offlineFarmIds && offlineFarmIds.length > 0);
    const needsFarmInitOffline = hasFarmsOffline && !isCorporate;

    setLoadingProgress(2, APP_LOADING_STEPS);
    if (!skipLoadingStateReset || !needsFarmInitOffline) {
      setLoading("idle");
    }

    if (!hasFarmsOffline) {
      return { user: cachedUser, needsFarmInit: false };
    }
    return {
      farms: offlineFarms,
      user: cachedUser,
      needsFarmInit: needsFarmInitOffline,
    };
  }

  let user: User;
  try {
    user = await queryClient.fetchQuery({
      queryKey: queryKeys.user.detail(jwtDecoded.user),
      queryFn: () => fetchUserByIdQuery(jwtDecoded.user),
      staleTime: 1000 * 60 * 5,
    });
    // Store user in Zustand
    setUser(user);
    setLoadingProgress(1, APP_LOADING_STEPS);

    if (user && user.guid) {
      // Fetch file entries in background (non-blocking)
      void fetchFileEntriesForUserQuery(user.guid, queryClient).then(
        (fileEntries) => {
          queryClient.setQueryData(
            queryKeys.fileEntries.byUser(user.guid),
            fileEntries
          );
        }
      );
    }
  } catch (e) {
    setLoading("idle");
    throw e;
  }

  // Redirect only after sign in, to make sure the token is valid
  const redirectUrl = getRedirectUrlWithToken();
  if (user && user.guid && redirectUrl) {
    redirect();
    return {};
  }

  if (user.lang) {
    setLocale(user.lang);
  }
  const farmIds = user.farms;

  if (jwtDecoded.impersonated) {
    analytics.opt_out_tracking();
  }

  analytics.identify(jwtDecoded.user);

  // With Mixpanel's identity merge, we need to explicitly store the user guid as
  // a property and then use it in reports; because distinct id can be random uuids
  analytics.register({
    user_id: jwtDecoded.user,
    user_type: user.userType,
    gitsha: import.meta.env.VITE_GITSHA,
  });

  // Setup device registration for push notifications (non-blocking)
  if (shouldRegisterDevice()) {
    void setupPushNotificationsAndRegisterDevice()
      .then(() => markDeviceAsRegistered())
      .catch((error) =>
        console.error("Failed to setup device registration:", error)
      );
  }

  // ========== PARALLEL FETCH: All independent data at once ==========
  // These fetches have no dependencies on each other, so run them all in parallel
  setLoadingStatus("Loading your data...");

  const isCorporateUser =
    user?.corporations?.length && user?.corporations?.length > 0;

  // Build array of parallel fetch promises
  const parallelFetches: Promise<unknown>[] = [
    queryClient.fetchQuery({
      queryKey: queryKeys.programmeActions.all,
      queryFn: fetchProgrammeActionsQuery,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.quickAssessment.results,
      queryFn: fetchQuickAssessmentResultsQuery,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.defaultStockClasses.all,
      queryFn: () => fetchDefaultStockClassesQuery(queryClient),
      staleTime: 1000 * 60 * 60,
    }),
  ];

  // Corporate user dashboards
  if (isCorporateUser) {
    parallelFetches.push(
      queryClient.fetchQuery({
        queryKey: queryKeys.dashboards.accessibleFarmerProgrammes,
        queryFn: fetchAccessibleFarmerProgrammesQuery,
      })
    );
  }

  // Submitted actions (requires user email)
  if (user.mail) {
    parallelFetches.push(
      queryClient.fetchQuery({
        queryKey: queryKeys.programmeActions.submitted(user.mail),
        queryFn: () => fetchSubmittedActionsQuery(user.mail),
      })
    );
  }

  // Associated users (v2 list endpoint - fetches all at once)
  parallelFetches.push(fetchAllAssociatedUsersQuery(queryClient));

  // Farms
  let farms: Farm[] = [];
  if (farmIds && farmIds.length > 0) {
    parallelFetches.push(
      Promise.all(
        farmIds.map((id) => fetchFarmByIdQuery(id, queryClient))
      ).then((fetchedFarms) => {
        farms = fetchedFarms;
        return farms;
      })
    );
  }

  // Execute all fetches in parallel
  await Promise.all(parallelFetches);
  setLoadingProgress(2, APP_LOADING_STEPS);

  // Load certification modal state from localStorage
  useUIStore.getState().loadCertificationProgramApplicationModalState();

  // Determine if farm initialization will be needed
  const hasFarms = farmIds && farmIds.length > 0;
  const needsFarmInit = hasFarms && !isCorporateUser;

  // Only set loading to idle if:
  // 1. Farm init won't follow (corporate users or no farms), OR
  // 2. Caller explicitly doesn't want us to skip the reset
  if (!skipLoadingStateReset || !needsFarmInit) {
    setLoading("idle");
  }
  setAuthenticated(true);

  if (!hasFarms) {
    return { user, needsFarmInit: false };
  }

  return { farms, user, needsFarmInit };
}

/**
 * Initialize a farm using React Query.
 * Fetches all farm data and populates React Query cache.
 * Optimized to fetch independent data in parallel.
 *
 * Initializes a specific farm.
 *
 * @param farm - The farm to initialize
 * @param options - Optional configuration
 * @param options.force - If true, re-initialize even if same farm is already loaded
 */
export async function initializeFarmRQ(
  farm: Farm,
  options?: { force?: boolean }
): Promise<void> {
  const {
    setLoadingStatus,
    setLoadingProgress,
    setCurrentFarmId,
    setLoading,
    currentFarmId,
    loading,
  } = useAppStore.getState();
  const { force = false } = options ?? {};

  // Skip re-initialization if:
  // 1. Same farm is already set as current
  // 2. We're not forcing a refresh
  //
  // Note: during app load multiple callers may race to initialize the same farm.
  // Once currentFarmId is set, we should treat further calls as no-ops.
  if (currentFarmId === farm.guid && !force) {
    return;
  }

  // Resolve the device's real connectivity (Network.getStatus) before any
  // onlineManager.isOnline() gate below. On a cold start the bridge has not
  // applied the native status yet, so isOnline() returns its stale default
  // (online); without this await an offline cold start skips the offline
  // early-return and runs the farm network fetches (fetchFieldsByIdQuery),
  // which hang the loading screen and crash re-processing the persisted
  // (already-processed) field cache.
  await awaitOnlineStatus();

  // Set loading state to show loading screen during farm initialization
  // (may already be pending from app init, which is fine)
  setLoading("pending");
  // Set loading status - use farm name for personalized feedback
  setLoadingStatus(`Loading ${farm.name}...`);
  setCurrentFarmId(farm.guid);
  analytics.register({ farm_id: farm.guid });
  setLoadingProgress(2, APP_LOADING_STEPS); // Continue from app init progress (step 2)

  // Do not wipe fields/animal-groups/farms on farm switch. List hooks filter
  // by current farm, and clearing the whole domain removes other farms' detail
  // entries from memory and the next persisted snapshot — breaking offline
  // switch back to a previously visited farm.

  // Store the farm in cache
  queryClient.setQueryData(queryKeys.farms.detail(farm.guid), farm);

  // Offline: the field, animal-group, stock-class and move-event data is in the
  // persisted cache (restored on load). Skip the network fetches that would
  // pause and hang the loading screen, and mark loading complete.
  if (!onlineManager.isOnline()) {
    setLoadingProgress(APP_LOADING_STEPS, APP_LOADING_STEPS);
    setLoadingStatus("");
    setLoading("idle");
    return;
  }

  // Track completion of parallel operations for granular progress updates
  let fieldsLoaded = false;
  let animalsLoaded = false;

  const updateProgressStatus = () => {
    if (fieldsLoaded && animalsLoaded) {
      setLoadingStatus("Finishing up...");
      setLoadingProgress(4, APP_LOADING_STEPS);
    } else if (fieldsLoaded) {
      setLoadingStatus("Loading animals...");
      setLoadingProgress(3, APP_LOADING_STEPS);
    } else if (animalsLoaded) {
      setLoadingStatus("Loading fields...");
      setLoadingProgress(3, APP_LOADING_STEPS);
    }
  };

  // ========== Load all farm data in parallel ==========
  const farmDataPromises: Promise<unknown>[] = [];

  // Arable fields - update progress when complete
  farmDataPromises.push(
    fetchArableFieldsByFarmIdQuery(farm.guid, queryClient).then((result) => {
      fieldsLoaded = true;
      updateProgressStatus();
      return result;
    })
  );

  // Grass/pasture fields - mark as part of fields loading
  if (farm.fields && farm.fields.length !== 0) {
    const fieldIds = farm.fields;
    farmDataPromises.push(
      queryClient
        .fetchQuery({
          queryKey: queryKeys.fields.byFarm(farm.guid),
          queryFn: () => fetchFieldsByIdQuery(fieldIds, queryClient),
          staleTime: 1000 * 60 * 5,
        })
        .then((result) => {
          // Fields already marked as loaded by arable fields, but ensure it's set
          fieldsLoaded = true;
          updateProgressStatus();
          return result;
        })
    );
  }

  // Animal groups and their stock classes (chained to avoid separate phase)
  if (farm.animalGroups && farm.animalGroups.length > 0) {
    farmDataPromises.push(
      fetchAnimalGroupsByIdQuery(farm.animalGroups, queryClient).then(
        async (animalGroups) => {
          // Load stock classes for all animal groups
          const allStockClassIds = animalGroups.flatMap(
            (ag) => ag.stockClasses || []
          );
          if (allStockClassIds.length > 0) {
            await fetchStockClassesByIdQuery(allStockClassIds, queryClient);
          }
          animalsLoaded = true;
          updateProgressStatus();
          return animalGroups;
        }
      )
    );
    // Prefetch move events for every group so the move dialog can find the
    // currently-open event offline. Fire-and-forget so it does not delay the
    // initial load; the helper never rejects.
    void prefetchAnimalGroupMoveEventsQuery(farm.animalGroups, queryClient);
  } else {
    // No animal groups to load, mark as complete
    animalsLoaded = true;
  }

  // Default stock classes (reference data)
  farmDataPromises.push(
    queryClient.fetchQuery({
      queryKey: queryKeys.defaultStockClasses.all,
      queryFn: () => fetchDefaultStockClassesQuery(queryClient),
      staleTime: 1000 * 60 * 60,
    })
  );

  // Start with "Loading fields..." since that's typically the first to start
  setLoadingStatus("Loading fields...");
  setLoadingProgress(3, APP_LOADING_STEPS);

  // Execute all in parallel
  await Promise.all(farmDataPromises);

  // ========== Background tasks (non-blocking) ==========
  // Load field tiles and growth rate in background
  void (async () => {
    // Get arable field tiles
    const arableFields = getAllArableFieldsFromCache(queryClient, farm.guid);
    const arableTileIds = getLatestArableFieldTilesFromArray(arableFields);

    // Get grass/pasture field tiles
    const fields = getAllFieldsFromCache(queryClient, farm.guid);
    const fieldTileIds = getLatestFieldTilesFromArray(fields);

    // Fetch all tiles in parallel (already batched internally)
    const tilePromises: Promise<unknown>[] = [];
    if (arableTileIds.length > 0) {
      tilePromises.push(fetchTilesByIdsQuery(arableTileIds, queryClient));
    }
    if (fieldTileIds.length > 0) {
      tilePromises.push(fetchTilesByIdsQuery(fieldTileIds, queryClient));
    }
    await Promise.all(tilePromises);

    // Invalidate fields with no tiles (mark them for re-processing)
    const arableFieldsWithNoTiles =
      getArableFieldsWithNoTilesFromArray(arableFields);
    arableFieldsWithNoTiles.forEach((fieldId) =>
      invalidateArableFieldData(fieldId, queryClient)
    );

    const fieldsWithNoTiles = getFieldsWithNoTilesFromArray(fields);
    fieldsWithNoTiles.forEach((fieldId) =>
      invalidateFieldData(fieldId, queryClient)
    );

    // Fetch growth rate for the first field (used for farm metrics)
    if (farm.fields && farm.fields.length > 0) {
      try {
        const growthRateResponse = await fetchApi(
          `fields/${farm.fields[0]}/growthRate`
        );
        if (
          growthRateResponse &&
          typeof growthRateResponse.growthRate === "number"
        ) {
          const growthRate: number = growthRateResponse.growthRate;
          useUIStore.getState().setGrowthRate(growthRate);
        }
      } catch (error) {
        // Growth rate fetch is not critical, just log and continue
        console.warn("Failed to fetch growth rate:", error);
      }
    }
  })();

  // Mark loading complete
  setLoadingProgress(APP_LOADING_STEPS, APP_LOADING_STEPS);
  setLoadingStatus("");
  setLoading("idle");
}

/**
 * Fetch a farm by ID using React Query.
 * This is a standalone fetch that doesn't do full initialization.
 *
 * @param farmId - The farm ID to fetch
 * @returns The fetched farm
 */
export async function fetchFarmByIdRQ(farmId: string): Promise<Farm> {
  return await fetchFarmByIdQuery(farmId, queryClient);
}

/**
 * Fetch animal groups by their IDs using React Query.
 *
 * @param animalGroupIds - Array of animal group IDs to fetch
 * @returns The fetched animal groups
 */
export async function fetchAnimalGroupsByIdRQ(
  animalGroupIds: string[]
): Promise<Awaited<ReturnType<typeof fetchAnimalGroupsByIdQuery>>> {
  return await fetchAnimalGroupsByIdQuery(animalGroupIds, queryClient);
}

/**
 * Finish loading state in Zustand.
 */
export function finishLoadingStateRQ(): void {
  const { setLoading, setLoadingStatus, setLoadingProgress } =
    useAppStore.getState();
  setLoading("idle");
  setLoadingStatus("");
  setLoadingProgress(0, 0);
}

/**
 * Switch to a different farm using React Query.
 * Fetches the farm and initializes it.
 *
 * @param farmId - The ID of the farm to switch to
 */
export async function switchFarmRQ(farmId: string): Promise<void> {
  const farm = await fetchFarmByIdQuery(farmId, queryClient);
  await initializeFarmRQ(farm);
}
